/* tslint:disable:import-spacing component-class-suffix no-inferrable-types variable-name
   typedef-whitespace component-selector no-trailing-whitespace */

import {
  AfterViewInit,
  OnDestroy,
  Component,
  HostListener,
  Input
}                                    from '@angular/core';

import {
  BehaviorSubject,
  Subscription
}                                    from 'rxjs';

import {
  Diversion,
}                                    from '@/models/scenario/diversion';
import {TrackIngress}                from '@/helpers/track.ingress';

import {NumberRenderer}              from '@/helpers/number.renderer';

import {Scenario}                    from '@/models/scenario/scenario';
import {Logger}                      from '@/util/logger';
import {UnDoRedo}                    from '@/util/un.do.redo';
import {DiversionNameRenderer}       from './diversion.name.renderer';
import {WorkDecisionPointRenderer}   from '../workblox/work.decision.point.renderer';
import {DiversionWorkRenderer}       from './diversion.work.renderer';
import {TrackRenderer}               from '@/helpers/track.renderer';
import {TrainsRenderer}              from '@/helpers/trains.renderer';
import {DiversionChange}             from '@/screens/ide/diversion/diversion.change';
import {SummaryTableSelectCommand}   from './summary.table.select.command';
import {SummaryTableUnSelectCommand} from './summary.table.un.select.command';
import {DiversionInlineEdit}         from './diversion.inline.edit';
import {ICommand}                    from '@/util/i.command';

import 'ag-grid-community';
import 'ag-grid-enterprise';

import {
  CsvExportParams,
  ExcelExportParams,
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent, ProcessCellForExportParams,
  RowClickedEvent,
  RowNode
} from "ag-grid-community";
import {basename} from "@/util/util";
import {Train} from "@/models/scenario/train";
import {DecisionPoint} from "@/models/scenario/decision.point";
import {WorkBlock} from "@/models/scenario/work.block";
import {Track} from "@/models/scenario/track";
import {TrainDirection} from "@/models/scenario/train.class";

@Component({
  selector: 'diversion-summary-table',
  template: `
    <div id="diversion-table-container_{{uuid}}" class="diversion-table-container" style="">
      <ag-grid-angular id="ag-grid-diversion-table_{{uuid}}" style="width: 100%; height: 100%;"
        class="ag-theme-balham-dark ag-grid-diversion-table"
        [rowData]="rowData"
        [columnDefs]="defaultColDefs"
        [defaultColDef]="defaultColDef"
        [context]="context"
        [animateRows]="true"
        [enableRangeSelection]="true"
        [rowSelection]="'single'"

        [allowContextMenuWithControlKey]="true"
        [getContextMenuItems]="getContextMenuItems"

        (gridReady)="onGridReady($event)"
        (selectionChanged)="onSelectionChanged()"
        (rowClicked)="onRowClicked($event)"
      >
      </ag-grid-angular>
    </div>
    `
})

/**
 *
 * @class DiversionSummaryTable
 *
 * @brief @brief this table shows a tabular summary of all diversions in the scenario
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      July 2019
 *
 */
export class DiversionSummaryTable implements AfterViewInit, OnDestroy {

  @Input() uuid: number = 0;

  /** the history manager (not owned by us */
  private _history: UnDoRedo     = null;

  /** @var _scenario the scenario model */
  private _scenario: Scenario = null;

  /** the currently active row/selection/Diversion */
  private selectedDiversion: BehaviorSubject<DiversionChange>     = null;
  private         selectSub: Subscription                         = null;

  private    lastInlineEdit: BehaviorSubject<DiversionInlineEdit> = null;
  private           editSub: Subscription                         = null;

  /* the row that was previously selected */
  private oldSelection: any = null;

  /** the parent controller/view must provide us with a means to capture the editor input */
  public inputCapture: () => DiversionChange;

  /** the parent controller/view must provide usu with a means to restore the editor input */
  public inputRestore: (snapshot: DiversionChange) => void;

  /** the API for the widget */
  private          api: GridApi  = null;

  private isFullScreen: boolean = false;

  public context = {

    diversionTable: this

  };

  public defaultColDef = {
    sortable:  true,
    filter:    true,
    resizable: true
  };

  public rowData = [];


  public defaultColDefs: any = [

    {
      headerName: 'ID',
      field: 'id',
      editable: false,
      width: 72
    },

    {
      headerName: 'Name',
      field: 'name',
      cellRenderer: DiversionNameRenderer,
      editable: false,
      width: 150,
    },

    {
      headerName: 'Trains',
      field: 'trains',
      cellRenderer: TrainsRenderer,
      editable: false,
      width: 150,
    },

    {
      headerName: 'Work Block',
      field: 'work',
      cellRenderer: DiversionWorkRenderer,
      editable: false,
      width: 150,
    },

    {
      headerName: 'Start',
      field: 'start_loc',
      cellRenderer: WorkDecisionPointRenderer,
      editable: false,
      width: 150
    },
    {
      headerName: 'Start Track',
      field: 'start_track',
      cellRenderer: TrackRenderer,
      editable: false,
      width: 150,
      columnGroupShow: 'open'
    },

    {
      headerName: 'End',
      field: 'end_loc',
      cellRenderer: WorkDecisionPointRenderer,
      editable: false,
      width: 150
    },
    {
      headerName: 'End Track',
      field: 'end_track',
      cellRenderer: TrackRenderer,
      editable: false,
      width: 150,
      columnGroupShow: 'open'
    },

    {
      headerName: 'Duration',
      field: 'duration',
      cellRenderer: NumberRenderer,
      editable: false,
      width: 150,
    },

    {
      headerName: 'Std Dev.',
      field: 'stddev',
      cellRenderer: NumberRenderer,
      editable: false,
      width: 150,
    }

  ];

  public onGridReady(params: GridReadyEvent): void {

    this.api       = params.api;
  }

  /** helper to fetch the actual Diversion for the given row */
  public diversionOfRow(data: any): Diversion {

    const id = data?.id ? data.id : 0;

    return Array.from(this._scenario.diversions().values()).
      find(x => x._id === id);
  }

  /** allow selection from history */
  public doSelection(selection: DiversionChange) {

    /*
     * to allow user inputs to sometimes be different from the current
     * selection; i.e. no selection in summary view, and the user is
     * editing a completely new block...we use the 'snapshot' field; when
     * we emit a work block change...the input editor will look for the
     * snapshot field as an override; if its there, it uses those values
     * rather than the thing being selected.
     *
     * the upshot is that all we have to, is emit the selection (for the
     * other sub-panels), and locally select the implied row...or clear
     * the selection if there is nothing selected.
     *
     * We don't have to worry about interaction / timing with the other agGrid
     * events...because we haven't added any mechanics there.  It's all done
     * via RowClicked event, effectively doSelection() is  fake user click
     * on the row...but from history.
     *
     */

    /* adjust the summary table selection to this point in the history */

    if ((selection === null) || (selection?.id === null) || isNaN(selection.id)) {

      /* there is no row selected, just clear the selection */

      this.api.deselectAll();

      this.oldSelection = null;

    } else {

      /* select the implied row */

      const div: Diversion = this.diversionOfRow(selection);

      this.api.forEachNode((node: RowNode): void => {

        if (node.data.id !== div._id) {

          return;
        }

        node.setSelected(true, true);

        this.oldSelection = node.data;

      });

    }

    /* emit the selection so that other panels also sync up with this position in the history */

    this.selectedDiversion.next(selection);

  }

  /** fetch the current selection */
  public getSelection(): DiversionChange {

    const  data: DiversionChange = new DiversionChange({
      src:         null,
      id:          null,
      name:        null,
      type:        null,
      work:        null,
      start:       null,
      startTrack:  null,
      end:         null,
      endTrack:    null,
      duration:    null,
      stdDev:      null,
      trains:      null,
      description: null,
      snapshot:    this.inputCapture(),
      model:       null
    });

    /* is anything selected? */

    if (this.oldSelection === null) {

      /* we have the selection */

      return data;
    }

    /* if something is selected, then we fill in selection */

    const div: Diversion = this.diversionOfRow(this.oldSelection);

    data.src               = this;
    data.id                = div._id;
    data.name              = div._name;
    data.type              = div._type;
    data.work              = div._work;
    data.start             = div._from;
    data.startTrack        = new TrackIngress(div._from, div._fromTrack);
    data.end               = div._to;
    data.endTrack          = new TrackIngress(div._to, div._toTrack);
    data.duration          = div._duration;
    data.stdDev            = div._stddev;
    data.trains            = div._trains;
    data.description       = div._description;

    return data;
  }

  /** clear the selection (if any) */
  public clearSelection() {

    if (this.oldSelection === null) {

      /* nothing to do */

      return ;
    }

    /* the old value (the existing selection) might be empty */

    const  empty: DiversionChange = new DiversionChange({
      src:         null,
      id:          null,
      name:        null,
      type:        null,
      work:        null,
      start:       null,
      startTrack:  null,
      end:         null,
      endTrack:    null,
      duration:    null,
      stdDev:      null,
      trains:      null,
      description: null,
      snapshot:    null,
      model:       null
    });

    let oldValue: DiversionChange;
    let newValue: DiversionChange;
    let div:      Diversion;

    /* capture the old value */

    /* snapshot is not null, so emitting this one will make the user input take on the snapshot */

    div      = this.diversionOfRow(this.oldSelection);
    oldValue = new DiversionChange({
      src:         this,
      id:          div._id,
      name:        div._name,
      type:        div._type,
      work:        div._work,
      start:       div._from,
      startTrack:  new TrackIngress(div._from, div._fromTrack),
      end:         div._to,
      endTrack:    new TrackIngress(div._to, div._toTrack),
      duration:    div._duration,
      stdDev:      div._stddev,
      trains:      div._trains,
      description: div._description,
      snapshot:    this.inputCapture()
    });

    /* capture the new value */

    newValue = empty;

    /* de-select */

    this.api.deselectAll();

    this.oldSelection = null;

    /* it's now safe to signal the selection change */

    this.selectedDiversion.next(newValue);

    /* track the history of selecting something new */

    const cmd: ICommand = new SummaryTableUnSelectCommand(oldValue, newValue, this);

    /* track the change */

    this._history.insert(cmd);
  }

  /**
   *  handle a user clicking a row, which just toggles it to selected or no selection at all
   *  because we only allow single row selection.
   *
   */
  public onRowClicked(e: RowClickedEvent) {

    /* the old value (the existing selection) might be empty */

    const  empty: DiversionChange = new DiversionChange({
      src:         null,
      id:          null,
      name:        null,
      type:        null,
      work:        null,
      start:       null,
      startTrack:  null,
      end:         null,
      endTrack:    null,
      duration:    null,
      stdDev:      null,
      trains:      null,
      description: null,
      snapshot:    null,
      model:       null
    });

    let oldValue: DiversionChange;
    let newValue: DiversionChange;
    let div:      Diversion       = null;

    /* we only do single select, so if the row we click on is already selected...we are deselecting */

    if ((this.oldSelection !== null) && (this.oldSelection.id === e.data.id)) {

      /* capture the old value */

      /* snapshot is not null, so emitting this one will make the user input take on the snapshot */

      div      = this.diversionOfRow(e.data);
      oldValue = new DiversionChange({
        src:         this,
        id:          div._id,
        name:        div._name,
        type:        div._type,
        work:        div._work,
        start:       div._from,
        startTrack:  new TrackIngress(div._from, div._fromTrack),
        end:         div._to,
        endTrack:    new TrackIngress(div._to, div._toTrack),
        duration:    div._duration,
        stdDev:      div._stddev,
        trains:      div._trains,
        description: div._description,
        snapshot:    this.inputCapture()
      });

      /* capture the new value */

      newValue = empty;

      /* de-select */

      this.api.deselectAll();

      this.oldSelection = null;

    }  else {

      /* capture the old state */

      if (this.oldSelection === null) {

        oldValue          = Object.assign({}, empty);
        oldValue.snapshot = this.inputCapture();

      } else {

        /* snapshot is not null, so emitting this one will make the user input take on the snapshot */

        div     = this.diversionOfRow(this.oldSelection);
        oldValue  = new DiversionChange({
          src:         this,
          id:          div._id,
          name:        div._name,
          type:        div._type,
          work:        div._work,
          start:       div._from,
          startTrack:  new TrackIngress(div._from, div._fromTrack),
          end:         div._to,
          endTrack:    new TrackIngress(div._to, div._toTrack),
          duration:    div._duration,
          stdDev:      div._stddev,
          trains:      div._trains,
          description: div._description,
          snapshot:    this.inputCapture()
        });

        /*  agGrid will do its usual selection change events */
      }

      /* capture the new state, when snapshot is null the inputs will be newValue */

      div      = this.diversionOfRow(e.data);
      newValue = new DiversionChange({
        src:         this,
        id:          div._id,
        name:        div._name,
        type:        div._type,
        work:        div._work,
        start:       div._from,
        startTrack:  new TrackIngress(div._from, div._fromTrack),
        end:         div._to,
        endTrack:    new TrackIngress(div._to, div._toTrack),
        duration:    div._duration,
        stdDev:      div._stddev,
        trains:      div._trains,
        description: div._description,
        snapshot:    null
      });

      this.oldSelection = e.data;
    }

    /* it's now safe to signal the selection change */

    this.selectedDiversion.next(newValue);

    /* track the history of selecting something new */

    const cmd: ICommand = new SummaryTableSelectCommand(oldValue, newValue, this);

    /* track the change */

    this._history.insert(cmd);
  }

  public onSelectionChanged(): void {

    if (this.oldSelection === null) {

      return;
    }

    /* keep the currently selected row selected */

    this.api.forEachNode((node: RowNode): void => {

      if (node.data.id === this.oldSelection.id) {

        node.setSelected(true, true);
      }

    });
  }

  public toggleFullScreen() {

    if (this.isFullScreen) {

      Logger.debug('[diversion/table] exiting full screen...');

      document.exitFullscreen();

    } else {

      Logger.debug('[diversion/table] going full screen...');

      const elem = document.getElementById(`ag-grid-diversion-table_${this.uuid}`);

      if (elem.requestFullscreen) {

        elem.requestFullscreen();
      }
    }

  }

  public getContextMenuItems(params: GetContextMenuItemsParams): any {

    const     context: any[] = [];
    const divTimeTable        = params.context.diversionTable;

    if (context.length !== 0) {

      context.push('separator');
    }

    /* full screen control */

    let label: string = 'Show Full Screen';

    if (divTimeTable.isFullScreen) {

      label = 'Exit Full Screen';
    }

    context.push( {
      name: label,
      action: () => {

        divTimeTable.toggleFullScreen();
      }
    });

    context.push('separator');

    const general: any[] = [

      'copy',
      'copyWithHeaders',
      'paste',

      'separator',

      'expandAll',
      'contractAll',

      'separator'
    ];

    context.push.apply(context, general);

    /* add in our custom export functions ... */

    context.push( {
      name: `CSV Export`,
      action: () => {

        divTimeTable.csvExport();
      }
    });

    context.push( {
      name: `Excel Export`,
      action: () => {

        divTimeTable.excelExport();
      }
    });

    /* add remaining common / generic actions */

    const general2: any[] = [

      'separator',

      'autoSizeAll'
    ];

    /* pass back the merged menu */

    context.push.apply(context, general2);

    return context;
  }

  ngOnDestroy(): void {

    if (this.selectSub !== null) {

      /* unsubscribe our observer */

      this.selectSub.unsubscribe();
    }

    if (this.editSub !== null) {

      /* unsubscribe our observer */

      this.editSub.unsubscribe();
    }
  }

  /** @brief first time setup of view */
  ngAfterViewInit() {

    Logger.debug('[diversion/table] view init...');

    this.clear();

    const        elem = document.getElementById(`ag-grid-diversion-table_${this.uuid}`);
    const divTimeTable = this;

    elem.addEventListener('fullscreenchange', () => {

      divTimeTable.isFullScreen = document.fullscreenElement != null;

      Logger.debug('[diversion/table] (elem) flipped full screen mode: ', divTimeTable.isFullScreen);

    }, true);
  }

  /** @brief reset the view to empty */
  public clear() {

    this._scenario = null;
  }

  /** @brief standard destructor */
  destroy() {

    Logger.debug('[diversion/table] cleaning up...');

  }

  /** format one of the table column values as a simple string (i.e. for export) */
  public format(value: any): string {

    if (Array.isArray(value) && value.length > 0 && value[0] instanceof Train) {

      /* format a train list */

      return (value as Train[]).map((train: Train): string => {

        return train.quickName();

      }).join(',');

    }

    if (value instanceof WorkBlock) {

      const wb: WorkBlock = value as WorkBlock;

      return '#' + wb._id + ' ' + wb._name.trim().toUpperCase();
    }

    if (value instanceof TrackIngress) {

      return `${value.track._id} ${Track.typeToString(value.track._type)}${value.track._track}`;
    }

    if (value instanceof DecisionPoint) {

      let   name: string = value._name.trim();
      const mile: string = value._mileage.toFixed(2);

      if (name.length >= 16) {

        name = name.substring(0, 16) + '...';
      }

      return `${name} - ${mile}mi`;
    }

    /* fell through, we don't know how to format this thing. */

    Logger.error('[diversion/table] can not format cell', value);

    return '';
  }

  /** export the table to a CSV download */
  public csvExport(): void {

    let dst: DiversionSummaryTable = this;

    Logger.debug('[diversion/table] CSV export...');

    /* ref: https://www.ag-grid.com/angular-data-grid/csv-export/#csvexportparams */

    let fileName: string = `${basename(dst._scenario._tag)}.csv`;

    let params: CsvExportParams = {
      allColumns: true,
      fileName,
      processCellCallback: (params: ProcessCellForExportParams): string => {

        if (typeof params.value == 'number' || typeof params.value == 'string') {

          return params.value as string;
        }

        return dst.format(params.value);
      }
    };

    dst.api.exportDataAsCsv(params);
  }

  /** export the table to an Excel download */
  public excelExport(): void {

    let dst: DiversionSummaryTable = this;

    Logger.debug('[diversion/table] EXCEL export...');

    /* ref: https://www.ag-grid.com/angular-data-grid/csv-export/#csvexportparams */

    let fileName: string = `${basename(dst._scenario._tag)}.xlsx`;

    let params: ExcelExportParams = {
      allColumns: true,
      fileName,
      processCellCallback: (params: ProcessCellForExportParams): string => {

        if (typeof params.value == 'number' || typeof params.value == 'string') {

          return params.value as string;
        }

        return dst.format(params.value);
      }
    };

    dst.api.exportDataAsExcel(params);
  }

  /** take a reference to the shared history manager (provides undo/redo support) */
  public setHistory(history: UnDoRedo): void {

    this._history = history;
  }

  /** @brief set the table base on this scenario */
  setScenario(scenario: Scenario = null): boolean {

    Logger.debug('[diversion/table] setting scenario...');

    if (scenario === null) {

      this.clear();

      return true;
    }

    /* save the model */

    this._scenario                   = scenario;

    /* refresh the table based on currently selected train class */

    this.refreshTable();

    /* all done */

    return true;
  }

  /** handle single column edits in the edit input area */
  setInlineEditSubject(edit: BehaviorSubject<DiversionInlineEdit>) {

    if (this.editSub !== null) {

      /* unsubscribe old one */

      this.editSub.unsubscribe();
    }

    /* subscribe to this selection watcher ... */

    this.lastInlineEdit = edit;
    this.editSub        = edit.subscribe( (next: DiversionInlineEdit) => {

      this.inlineEdited(next);
    });
  }

  /** handle single column changes in the editing inputs */
  public inlineEdited(edit: DiversionInlineEdit) {

    if (edit === null) {

      /* nothing to do */

      return ;
    }

    Logger.debug('[diversion/table] inline edit', edit);

    let datum:        any = edit.value;
    let dataField: string = edit.field;

    /* get the row for this diversion */

    this.api.forEachNode((node: RowNode): void => {

      if (node.data.id !== edit.id) {

        return;
      }

      /* twitch the value (without any change events) */

      node.data[dataField] = datum;

      this.api.redrawRows({rowNodes: [node]});

    });

  }

  /** set a new observer to use for watching for selection changes */
  public setSelectionSubject(selection: BehaviorSubject<DiversionChange>) {

    if (this.selectSub !== null) {

      /* unsubscribe old one */

      this.selectSub.unsubscribe();
    }

    /* subscribe to this selection watcher ... */

    this.selectedDiversion = selection;
    this.selectSub         = selection.subscribe( (next: DiversionChange) => {

      this.setSelected(next);
    });

  }

  /** handle somebody change the selection somewhere */
  public setSelected(change: DiversionChange) {

    if (change === null) {

      /* just clear the selection */

      Logger.debug('[diversion/table:set] clearing selection');

      this.api.deselectAll();
      this.oldSelection = null;

      return ;
    }

    /* check that we aren't calling ourselves */

    if ( (change.src instanceof DiversionSummaryTable) && (change.src === this)) {

      /* from self, ignore infinite looping! */

      return ;
    }

  }

  /** @brief helper to update the table  */
  public refreshTable(): boolean {

    Logger.debug('[diversion/table] refreshing on', this._scenario);

    /* start with standard columns ... */

    const cd: any[] = [].concat([], this.defaultColDefs);

    /* ok, we can now tell agGrid what our columns are ... */

    this.api.setGridOption('rowData',    []);
    this.api.setGridOption('columnDefs', cd);

    /* now just build out the row data ... */

    this.rowData = [];

    let row: number = 0;

    for (const div of this._scenario.diversions().values()) {

      /* everything except active days */

      const rd: any = {
        id:          div._id,
        name:        div._name,
        trains:      div._trains,
        work:        div._work,
        start_loc:   div._from,
        start_track: new TrackIngress(div._from, div._fromTrack),
        end_loc:     div._to,
        end_track:   new TrackIngress(div._to, div._toTrack),
        duration:    div._duration,
        stddev:      div._stddev,
        row
      };

      row++;

      this.rowData.push(rd);

    } /* end of diversion walk */

    this.api.setGridOption('rowData', this.rowData);

    /* all done */

    return true;
  }

  @HostListener('window:resize')
  public resize() {

    Logger.debug('[diversion/table]', 'resizing...');

    /* for now use horizontal scroll, table is getting really wide */
    /* this.gridApi.sizeColumnsToFit(); */
  }

}
